import { action, computed, observable, reaction, makeObservable } from 'mobx';
import { X5LessonSnapshot, X8BlockSnapshot } from '../../../domain';
import { Disposers, HttpPromiseState, logger, urlHelper } from '../../../lib/helpers';
import { Classroom } from '../models/classroom-models';
import { ClassroomUtils } from '../models/classroom-utils';
import { ClassroomLoaders } from '../stores/classroom-loaders';
import { ClassroomHubStore } from '../stores/ClassroomHubStore';
import { routerStore } from 'lib/components';


export class TeacherClassroomSceneStore {
    @observable.ref
    classroomLoader: HttpPromiseState<Classroom> | null;

    hubStore: ClassroomHubStore;

    @observable.ref
    snapshotState: HttpPromiseState<X5LessonSnapshot> | null;

    @observable.ref
    snapshot: X5LessonSnapshot | null;

    private readonly disposers = new Disposers();

    constructor(readonly classroomId: number, readonly isPreview: boolean) {
        makeObservable(this);
        this.classroomLoader = null;

        this.hubStore = new ClassroomHubStore(classroomId, 'Teacher', isPreview);
        this.disposers.add(this.hubStore);

        this.snapshotState = this.snapshot = null;
        this.setup();
    }

    private setup() {
        this.disposers.add(
            reaction(
                () => this.classroomLoader?.value,
                this.onClassroomLoaded),
            reaction(
                () => this.snapshotState?.value,
                this.onLessonLoaded),
            reaction(
                () => [this.snapshot, this.hubStore.tasks.active, this.hubStore.keyValues.initialized],
                this.initializeContentItems),
        );
    }

    @action
    loadClassroom = () => {
        if (this.classroomLoader?.isPending)
            return;
        this.classroomLoader = ClassroomLoaders.Teacher.getClassroom(this.classroomId);
    };

    @action
    private onClassroomLoaded = () => {
        const state = this.classroomLoader;
        const isOk = state != null
            && state.isResolved
            && (state.value.Status === 'InProgress' || this.isPreview)
        if (!isOk)
            return;
        this.hubStore.start();
        this.loadLesson();
    };

    @computed
    get canConnectToHub(): boolean {
        if (!this.classroomLoader?.value) {
            return false;
        }
        const isOk = this.classroomLoader.value.Status === 'InProgress' || this.isPreview;
        if (!isOk) {
            return false;
        }
        if (this.hubStore.connection.dontReconnect) {
            return false;
        }
        return true;
    }

    @action
    loadLesson = () => {
        if (this.snapshotState?.isPending)
            return;
        if (this.snapshot?.Id === this.hubStore.lessonSnapshotId)
            return;
        this.snapshotState = ClassroomLoaders.Teacher.getLessonSnapshot(this.classroomId);
    };

    @action
    private onLessonLoaded = () => {
        const state = this.snapshotState;
        if (state === null || !state.isResolved)
            return;
        this.snapshot = ClassroomUtils.prepareLesson(state.value);
    };

    @action
    private initializeContentItems = () => {
        if (this.snapshot === null)
            return;
        if (!this.hubStore.tasks.active || !this.hubStore.keyValues.initialized)
            return;
        logger.log('TeacherClassroomSceneStore.initializeContentItems');
        if (this.hubStore.keyValues.get<boolean>('initialized') === true)
            return;
        ClassroomUtils.initializeLesson(this.hubStore.keyValues, this.snapshot);
        this.hubStore.keyValues.set<boolean>('initialized', true);
    };

    isRevealable(block: X8BlockSnapshot): boolean {
        return (block.BlockType === 'Pushable');
    }

    isRevealed(block: X8BlockSnapshot): boolean {
        return this.hubStore.revealedBlockIds.has(block.Id);
    }

    isAddedToHomework(block: X8BlockSnapshot): boolean {
        return this.hubStore.homeworkBlockIds.has(block.Id);
    }

    dispose() {
        this.disposers.dispose();
    }
}
